import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw } from "lucide-react";

export function MetaSettingsDialog({ open, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ app_id: "", app_secret: "", page_access_token: "", page_id: "", page_name: "", form_ids: [] });
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/meta/settings").then((r) => {
      setS(r.data);
      setF({ app_id: r.data.app_id, app_secret: "", page_access_token: "", page_id: r.data.page_id, page_name: r.data.page_name, form_ids: r.data.form_ids });
      if (r.data.page_id) setPages([{ id: r.data.page_id, name: r.data.page_name || r.data.page_id }]);
    }).catch((e) => toast.error(apiErr(e)));
  }, [open]);

  useEffect(() => {
    if (!open || !f.page_id || !s?.has_token) { setForms([]); return; }
    api.get(`/meta/forms?page_id=${f.page_id}`).then((r) => setForms(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [open, f.page_id, s?.has_token]);

  const save = async (close = true) => {
    setBusy(true);
    try {
      const { data } = await api.put("/meta/settings", f);
      setS(data); setF((x) => ({ ...x, app_secret: "", page_access_token: "" }));
      toast.success("Meta settings saved");
      onSaved?.();
      if (close) onClose();
      return data;
    } catch (e) { toast.error(apiErr(e)); return null; } finally { setBusy(false); }
  };

  const loadPages = async () => {
    if ((f.page_access_token || f.app_secret) && !(await save(false))) return;
    try { const { data } = await api.get("/meta/pages"); setPages(data); if (data.length === 1) setF((x) => ({ ...x, page_id: data[0].id, page_name: data[0].name })); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const toggleForm = (id) => setF((x) => ({ ...x, form_ids: x.form_ids.includes(id) ? x.form_ids.filter((y) => y !== id) : [...x.form_ids, id] }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-xl max-h-[92vh] overflow-y-auto" data-testid="meta-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight">Meta Lead Ads Settings</DialogTitle>
          <DialogDescription className="text-xs">Keys are stored encrypted on the server and never shown again after saving.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Meta App ID</Label><Input className="rounded-none mt-1" value={f.app_id} onChange={(e) => setF({ ...f, app_id: e.target.value })} data-testid="meta-app-id" /></div>
          <div><Label className="text-xs">App Secret {s?.has_secret && <span className="text-emerald-600">· saved</span>}</Label><Input type="password" className="rounded-none mt-1" placeholder={s?.has_secret ? "•••••••• (leave blank to keep)" : ""} value={f.app_secret} onChange={(e) => setF({ ...f, app_secret: e.target.value })} data-testid="meta-app-secret" /></div>
          <div><Label className="text-xs">Page Access Token {s?.has_token && <span className="text-emerald-600">· saved</span>}</Label><Input type="password" className="rounded-none mt-1" placeholder={s?.has_token ? "•••••••• (leave blank to keep)" : ""} value={f.page_access_token} onChange={(e) => setF({ ...f, page_access_token: e.target.value })} data-testid="meta-page-token" /></div>
          <div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Facebook Page</Label>
                <select className="mt-1 w-full h-9 border border-border bg-background px-2 text-sm" value={f.page_id} onChange={(e) => { const p = pages.find((x) => x.id === e.target.value); setF({ ...f, page_id: e.target.value, page_name: p?.name || "", form_ids: [] }); }} data-testid="meta-page-select">
                  <option value="">Select a page…</option>
                  {pages.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.id})</option>)}
                </select>
              </div>
              <Button variant="outline" className="rounded-none gap-1" onClick={loadPages} disabled={busy} data-testid="meta-load-pages"><RefreshCw className="h-4 w-4" /> Load pages</Button>
            </div>
          </div>
          {f.page_id && (
            <div>
              <Label className="text-xs">Lead Forms <span className="text-muted-foreground">(none ticked = all forms)</span></Label>
              <div className="mt-1 border border-border max-h-48 overflow-y-auto divide-y divide-border">
                {forms.length === 0 && <div className="p-3 text-xs text-muted-foreground">No forms loaded yet.</div>}
                {forms.map((fm) => (
                  <label key={fm.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted" data-testid={`meta-form-${fm.id}`}>
                    <input type="checkbox" checked={f.form_ids.includes(fm.id)} onChange={() => toggleForm(fm.id)} />
                    <span className="flex-1">{fm.name}</span><span className="text-[10px] uppercase text-muted-foreground">{fm.status}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none font-bold uppercase" onClick={() => save(true)} disabled={busy} data-testid="meta-settings-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
